import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LogOut, Building, Package, Factory, Bookmark } from 'lucide-react';
import LoadingState from '@/components/LoadingState';
import OrganisationPurchaseOrdersTab from '@/components/organisation/OrganisationPurchaseOrdersTab';
import OrganisationManufacturersTab from '@/components/organisation/OrganisationManufacturersTab';
import { verifyResellerSession, updateResellerAccess, clearResellerSession, consumeResellerMagicLinkEntry } from '@/utils/resellerSession';

interface SessionInfo {
  email: string;
  organisationId: string;
  organisationName: string;
  expiresAt: string;
  phoneVerified: boolean;
}

const ResellerPortal = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [showMagicLinkModal, setShowMagicLinkModal] = useState(false);

  useEffect(() => {
    verifySession();
  }, []);

  const verifySession = async () => {
    try {
      const data = await verifyResellerSession();

      if (!data.valid) {
        console.log('[ResellerPortal] Session invalid:', data.reason);
        setSessionExpired(true);
        setLoading(false);
        return;
      }

      const sessionOrgId = data.organisationId;

      setSessionInfo({
        email: data.email || '',
        organisationId: sessionOrgId || '',
        organisationName: data.organisationName || '',
        expiresAt: data.expiresAt || '',
        phoneVerified: data.phoneVerified || false
      });

      // Log portal access
      if (sessionOrgId) {
        await updateResellerAccess(sessionOrgId);
      }

      if (consumeResellerMagicLinkEntry()) {
        setShowMagicLinkModal(true);
      }

      setLoading(false);
    } catch (error) {
      console.error('[ResellerPortal] Error verifying session:', error);
      setSessionExpired(true);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await clearResellerSession();

      toast({
        title: 'Logged Out',
        description: 'You have been successfully logged out.'
      });

      navigate('/');
    } catch (error) {
      console.error('[ResellerPortal] Logout error:', error);
      navigate('/');
    }
  };

  if (loading) {
    return <LoadingState message="Verifying session..." />;
  }

  if (sessionExpired) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-xl">Session Expired</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground text-center">
              Your session has expired or is invalid. Please contact your administrator for a new access link.
            </p>
            <Button
              onClick={() => navigate('/')}
              className="w-full"
            >
              Return Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Dialog open={showMagicLinkModal} onOpenChange={setShowMagicLinkModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
              <Bookmark className="h-6 w-6 text-primary" />
            </div>
            <DialogTitle className="text-center">
              Your Magic Link only works once!
            </DialogTitle>
            <DialogDescription className="text-center text-base">
              Please bookmark this page.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button onClick={() => setShowMagicLinkModal(false)} className="w-full sm:w-auto">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="/lovable-uploads/d6f3215b-82a9-4aca-8941-f8a820e7beb5.png" 
                alt="Figabl Logo" 
                className="h-8 w-auto"
              />
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center space-x-3">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Building className="h-5 w-5 text-primary" />
                </div>
                <h1 className="text-xl font-semibold">{sessionInfo?.organisationName}</h1>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <Button variant="outline" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {sessionInfo?.organisationId && (
          <Tabs defaultValue="purchase-orders" className="w-full">
            <TabsList className="mb-6">
              <TabsTrigger value="purchase-orders" className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                Purchase Orders
              </TabsTrigger>
              <TabsTrigger value="manufacturers" className="flex items-center gap-2">
                <Factory className="h-4 w-4" />
                Manufacturers
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="purchase-orders">
              <OrganisationPurchaseOrdersTab
                organisation={{
                  id: sessionInfo.organisationId,
                  name: sessionInfo.organisationName
                }}
                isAdmin={false}
                isResellerPortal={true}
              />
            </TabsContent>
            
            <TabsContent value="manufacturers">
              <OrganisationManufacturersTab
                organisation={{
                  id: sessionInfo.organisationId,
                  name: sessionInfo.organisationName
                }}
              />
            </TabsContent>
          </Tabs>
        )}
      </main>

    </div>
  );
};

export default ResellerPortal;
